// Handles device detection

import { useState, useEffect } from 'react';
import { useSystemWindowSize } from './useSystemWindowSize';
import type { DeviceInfo } from '@airhead-games/shared/types/device';

// Constants for device detection
const DEVICE_BREAKPOINTS = {
  MOBILE_MAX: 768,
  TABLET_MAX: 1024,
} as const;

const BROWSER_PATTERNS = [
  { name: 'Chrome', pattern: /Chrome\/(\d+\.\d+)/, exclude: /Edg|OPR/ },
  { name: 'Edge', pattern: /Edg\/(\d+\.\d+)/ },
  { name: 'Firefox', pattern: /Firefox\/(\d+\.\d+)/ },
  {
    name: 'Safari',
    pattern: /Version\/(\d+\.\d+).*Safari/,
    exclude: /Chrome|Edg/,
  },
  { name: 'Opera', pattern: /OPR\/(\d+\.\d+)/ },
  { name: 'Brave', pattern: /Brave\/(\d+\.\d+)/ },
  { name: 'Samsung Internet', pattern: /SamsungBrowser\/(\d+\.\d+)/ },
] as const;

const OS_PATTERNS = {
  iOS: /iPhone OS (\d+_\d+)|iPad.*OS (\d+_\d+)|iPod.*OS (\d+_\d+)/,
  Android: /Android (\d+\.\d+)/,
  Windows: /Windows NT (\d+\.\d+)/,
  macOS: /Mac OS X (\d+[._]\d+)/,
  Linux: /Linux/,
  ChromeOS: /CrOS/,
  MacTouch: /mac/i,
} as const;

const DEVICE_PATTERNS = {
  mobile: /mobile|android|iphone|ipod|blackberry|iemobile|opera mini/i,
  tablet: /tablet|ipad/i,
} as const;

interface NetworkInformation extends EventTarget {
  readonly type?: string;
  readonly effectiveType?: string;
}

type DeviceNavigator = Navigator & {
  readonly connection?: NetworkInformation;
  readonly msMaxTouchPoints?: number;
};

// Core detection functions
const detectBrowser = () => {
  const userAgent = navigator.userAgent;

  for (const browser of BROWSER_PATTERNS) {
    if ('exclude' in browser && browser.exclude.test(userAgent)) continue;
    if (browser.pattern.test(userAgent)) {
      const match = userAgent.match(browser.pattern);
      return {
        name: browser.name,
        version: match ? match[1] : 'Unknown',
      };
    }
  }

  return { name: 'Unknown', version: 'Unknown' };
};

const detectOS = (): { name: string; version: string } => {
  const userAgent = navigator.userAgent;

  // iOS detection with version
  const iOSMatch = userAgent.match(OS_PATTERNS.iOS);
  if (iOSMatch) {
    return {
      name: 'iOS',
      version: iOSMatch[1]
        ? iOSMatch[1].replace('_', '.')
        : iOSMatch[2]?.replace('_', '.') || 'Unknown',
    };
  }

  // iPad Pro detection (Mac with touch)
  if (OS_PATTERNS.MacTouch.test(userAgent) && navigator.maxTouchPoints > 1) {
    return { name: 'iOS', version: 'iPad Pro' };
  }

  // Android detection with version
  const androidMatch = userAgent.match(OS_PATTERNS.Android);
  if (androidMatch) {
    return { name: 'Android', version: androidMatch[1] };
  }

  // Windows detection with version
  const windowsMatch = userAgent.match(OS_PATTERNS.Windows);
  if (windowsMatch) {
    return { name: 'Windows', version: windowsMatch[1] };
  }

  // macOS detection with version
  const macMatch = userAgent.match(OS_PATTERNS.macOS);
  if (macMatch) {
    return { name: 'macOS', version: macMatch[1].replace('_', '.') };
  }

  // ChromeOS detection
  if (OS_PATTERNS.ChromeOS.test(userAgent)) {
    return { name: 'ChromeOS', version: 'Unknown' };
  }

  // Linux detection
  if (OS_PATTERNS.Linux.test(userAgent)) {
    return { name: 'Linux', version: 'Unknown' };
  }

  return { name: 'Unknown', version: 'Unknown' };
};

// Touch and pointer detection utilities
const hasTouchCapability = (): boolean => {
  const deviceNavigator = navigator as DeviceNavigator;
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    (deviceNavigator.msMaxTouchPoints ?? 0) > 0
  );
};

const hasCoarsePointer = (): boolean => {
  return window.matchMedia('(pointer: coarse)').matches;
};

const hasFinePointer = (): boolean => {
  return window.matchMedia('(pointer: fine)').matches;
};

// Hybrid device type detection using multiple signals
const detectDeviceType = (width: number): 'mobile' | 'tablet' | 'desktop' => {
  const userAgent = navigator.userAgent.toLowerCase();
  const hasTouch = hasTouchCapability();
  const hasCoarse = hasCoarsePointer();
  const hasFine = hasFinePointer();

  // 1. Explicit mobile devices from user agent (most reliable)
  if (DEVICE_PATTERNS.mobile.test(userAgent)) {
    return 'mobile';
  }

  // 2. Explicit tablets from user agent (iPad, Android tablets)
  if (
    DEVICE_PATTERNS.tablet.test(userAgent) ||
    (hasTouch &&
      OS_PATTERNS.MacTouch.test(userAgent) &&
      navigator.maxTouchPoints > 1)
  ) {
    return 'tablet';
  }

  // 3. Touch + coarse pointer = likely mobile/tablet (use width to distinguish)
  // This catches devices that don't explicitly identify as mobile/tablet in UA
  if (hasTouch && hasCoarse) {
    return width < DEVICE_BREAKPOINTS.MOBILE_MAX ? 'mobile' : 'tablet';
  }

  // 4. Fine pointer + no touch = likely desktop (mouse/trackpad)
  if (hasFine && !hasTouch) {
    return 'desktop';
  }

  // 5. Fallback to width-based detection for edge cases
  // Handles unusual configurations or future devices
  if (width < DEVICE_BREAKPOINTS.MOBILE_MAX) return 'mobile';
  if (width < DEVICE_BREAKPOINTS.TABLET_MAX) return 'tablet';
  return 'desktop';
};

// Connection detection
const detectConnection = (
  deviceType: 'mobile' | 'tablet' | 'desktop'
): { type: string } => {
  // Try to detect actual connection type more reliably
  const connection = (navigator as DeviceNavigator).connection;
  if (!connection) return { type: 'unknown' };

  // First check actual connection type (most reliable for medium)
  if (connection.type) {
    switch (connection.type) {
      case 'wifi':
        return { type: 'wifi' };
      case 'ethernet':
        return { type: 'ethernet' };
      case 'cellular':
        // For cellular, use effectiveType to get speed details
        if (connection.effectiveType) {
          switch (connection.effectiveType) {
            case 'slow-2g':
              return { type: 'slow-2g' };
            case '2g':
              return { type: '2g' };
            case '3g':
              return { type: '3g' };
            case '4g':
              return { type: '4g' };
            case '5g':
              return { type: '5g' };
            default:
              return { type: 'cellular' };
          }
        }
        return { type: 'cellular' };
      case 'bluetooth':
        return { type: 'bluetooth' };
      case 'none':
        return { type: 'offline' };
      default:
        return { type: connection.type };
    }
  }

  // Fallback strategy when connection.type is unavailable
  if (deviceType === 'desktop') {
    // Desktop devices are almost always on WiFi or ethernet
    return { type: 'wifi' };
  }

  // For mobile/tablet, use effectiveType as it's more meaningful
  if (connection.effectiveType) {
    return { type: connection.effectiveType };
  }

  return { type: 'unknown' };
};

// Main detection function
const getDeviceInfo = (width: number): DeviceInfo => {
  // Ensure we're in a browser environment
  if (typeof window === 'undefined' || !window.navigator) {
    return {
      deviceType: 'desktop',
      deviceOS: { name: 'Unknown', version: 'Unknown' },
      deviceBrowser: { name: 'Unknown', version: 'Unknown' },
      deviceConnection: { type: 'unknown' },
    };
  }

  const deviceType = detectDeviceType(width);
  const deviceOS = detectOS();
  const deviceBrowser = detectBrowser();
  const deviceConnection = detectConnection(deviceType);

  return {
    deviceType,
    deviceOS,
    deviceBrowser,
    deviceConnection,
  };
};

// React hooks
export const useSystemDeviceDetection = (): DeviceInfo => {
  const { width } = useSystemWindowSize();
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>(() =>
    getDeviceInfo(width)
  );

  useEffect(() => {
    setDeviceInfo(getDeviceInfo(width));
  }, [width]);

  return deviceInfo;
};
